import { useState } from "react";
import { toast } from "sonner";
import { Check, ArrowRight, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { useI18n } from "@/lib/i18n";
import { submitInvestorEnquiry } from "@/lib/api";

export default function Investors() {
  const { t, isRTL } = useI18n();
  const thesis = t("investors.thesis");
  const phases = t("investors.phases");
  const f = t("investors.form");

  const [form, setForm] = useState({
    full_name: "", company: "", investor_type: "",
    email: "", mobile: "", investment_stage: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.company || !form.investor_type || !form.email || !form.mobile) {
      toast.error(t("common.required"));
      return;
    }
    setSubmitting(true);
    try {
      await submitInvestorEnquiry(form);
      toast.success(f.success, { id: "investor-enquiry-success" });
      setForm({ full_name: "", company: "", investor_type: "", email: "", mobile: "", investment_stage: "", message: "" });
    } catch {
      toast.error(t("business.form.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="investors" className="section bg-brand-sand relative overflow-hidden" data-testid="section-investors">
      <div className="absolute inset-0 -z-10 dot-pattern opacity-40" />

      <div className="container-x">
        <div className="max-w-3xl">
          <div className="eyebrow" style={{ color: "#8B6F1F" }}>{t("investors.eyebrow")}</div>
          <h2 className="h2 mt-4">{t("investors.title")}</h2>
          <p className="lede mt-5">{t("investors.lede")}</p>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-3xl bg-white border border-brand-line p-8 md:p-10">
            <div className="flex items-center gap-2 text-brand-gold">
              <Sparkles className="h-4 w-4" />
              <span className="text-xs uppercase tracking-[0.22em] font-semibold">Investment Thesis</span>
            </div>
            <ul className="mt-6 grid grid-cols-1 gap-3">
              {thesis.map((th, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-brand-charcoal/85">
                  <span className="mt-0.5 h-5 w-5 rounded-full bg-brand-charcoal text-brand-gold flex items-center justify-center shrink-0">
                    <Check className="h-3 w-3" />
                  </span>
                  <span>{th}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-3xl bg-brand-charcoal text-white p-8 md:p-10 relative overflow-hidden">
            <div className="absolute -top-16 -end-10 h-56 w-56 rounded-full bg-brand-gold/15 blur-3xl" />
            <div className="text-xs uppercase tracking-[0.22em] text-brand-gold font-semibold">
              {t("investors.roadmap_title")}
            </div>
            <ol className="mt-6 relative">
              <span className="absolute top-0 bottom-0 start-3 w-px bg-white/15" aria-hidden="true" />
              {phases.map((ph, i) => (
                <li key={i} className="relative ps-10 pb-5 last:pb-0" data-testid={`roadmap-phase-${i}`}>
                  <span className="absolute start-0 top-0 h-6 w-6 rounded-full bg-brand-gold text-brand-charcoal flex items-center justify-center text-[10px] font-extrabold">
                    {i + 1}
                  </span>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-brand-gold font-semibold">
                    {ph.p}
                  </div>
                  <div className="mt-1 text-base font-semibold text-white">{ph.t}</div>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* CTA row */}
        <div className="mt-10 flex flex-wrap items-center gap-3">
          <button
            onClick={() => document.getElementById("investor-form")?.scrollIntoView({ behavior: "smooth" })}
            className="btn-gold"
            data-testid="investor-cta-deck"
          >
            {t("investors.cta_deck")}
            <ArrowRight className={`h-4 w-4 ${isRTL ? "rotate-180" : ""}`} />
          </button>
          <button
            onClick={() => document.getElementById("investor-form")?.scrollIntoView({ behavior: "smooth" })}
            className="btn-outline"
            data-testid="investor-cta-explore"
          >
            {t("investors.cta_explore")}
          </button>
          <button
            onClick={() => document.getElementById("investor-form")?.scrollIntoView({ behavior: "smooth" })}
            className="btn-outline"
            data-testid="investor-cta-meet"
          >
            {t("investors.cta_meet")}
          </button>
        </div>

        {/* Form */}
        <motion.form
          id="investor-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-14 rounded-3xl border border-brand-line bg-white p-8 md:p-10"
          data-testid="investor-form"
        >
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            <div className="lg:col-span-2">
              <div className="text-xl font-bold text-brand-charcoal">{f.title}</div>
              <div className="text-sm text-brand-charcoal/60 mt-2">{f.subtitle}</div>
              <div className="mt-6 text-xs text-brand-charcoal/60 rounded-xl bg-brand-sand border border-brand-line p-4">
                {f.note}
              </div>
            </div>
            <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={f.full_name} value={form.full_name} onChange={update("full_name")} testid="inv-full-name" required />
              <Field label={f.company} value={form.company} onChange={update("company")} testid="inv-company" required />
              <SelectField
                label={f.investor_type}
                value={form.investor_type}
                onChange={update("investor_type")}
                options={f.investor_types}
                placeholder={f.investor_type_ph}
                testid="inv-type"
                required
              />
              <SelectField
                label={f.stage}
                value={form.investment_stage}
                onChange={update("investment_stage")}
                options={f.stages}
                placeholder={f.stage_ph}
                testid="inv-stage"
              />
              <Field label={f.email} type="email" value={form.email} onChange={update("email")} testid="inv-email" required />
              <Field label={f.mobile} value={form.mobile} onChange={update("mobile")} testid="inv-mobile" required />
              <div className="md:col-span-2">
                <TextAreaField label={f.message} value={form.message} onChange={update("message")} testid="inv-message" />
              </div>
              <div className="md:col-span-2">
                <button type="submit" disabled={submitting} className="btn-gold w-full md:w-auto" data-testid="inv-submit">
                  {submitting ? t("common.submitting") : f.submit}
                </button>
              </div>
            </div>
          </div>
        </motion.form>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", testid, required }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-brand-charcoal/70 mb-1.5">
        {label} {required && <span className="text-brand-red">*</span>}
      </span>
      <input type={type} value={value} onChange={onChange} required={required} data-testid={testid}
        className="w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-sm text-brand-charcoal placeholder:text-brand-charcoal/40 outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition-colors duration-150" />
    </label>
  );
}
function TextAreaField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-brand-charcoal/70 mb-1.5">{label}</span>
      <textarea rows={4} value={value} onChange={onChange} data-testid={testid}
        className="w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-sm text-brand-charcoal placeholder:text-brand-charcoal/40 outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition-colors duration-150 resize-none" />
    </label>
  );
}
function SelectField({ label, value, onChange, options, placeholder, testid, required }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-brand-charcoal/70 mb-1.5">
        {label} {required && <span className="text-brand-red">*</span>}
      </span>
      <select value={value} onChange={onChange} required={required} data-testid={testid}
        className="w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-sm text-brand-charcoal outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition-colors duration-150">
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
